import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CalltoactionService} from "@irb-common/components/calltoaction/calltoaction.service";
import {AppService} from "@irb/app.service";
import {ComScheduledRebalancesListGridLayoutService} from "@irb/irb-editors/editor-portfolio/com-scheduled-rebalances-list/com-scheduled-rebalances-list.grid-layout.service";
import {UtilityService} from "@irb-common/services/utility.service";
import {IrbRebalanceEvents} from "@irb/irb-rebalance/irb-rebalance.events";
import {ComIncludeInScheduledRebalanceDialogComponent} from "@irb/irb-editors/editor-portfolio/com-include-in-scheduled-rebalance-dialog/com-include-in-scheduled-rebalance-dialog.component";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';

@Component({ standalone: false,
    selector: 'irb-com-scheduled-rebalances-list',
    templateUrl: './com-scheduled-rebalances-list.component.html',
    styleUrls: ['./com-scheduled-rebalances-list.component.scss']
})
export class ComScheduledRebalancesListComponent implements OnInit {
    private _data: any;
    private _originalData: any;
    @Output() change = new EventEmitter<boolean>();
    public scheduledBalances: Array<any>;
    _isUnAuthorized: Boolean = false;
    rowCount: number;
    grid: any;
    gridOptions: any;
    defaultGridHeights: {
        height: string,
        minHeight: string,
        maxHeight: string,
        domLayout: string
    };
    frameworkComponents: any;

    constructor(private gls: ComScheduledRebalancesListGridLayoutService,
                private ctas: CalltoactionService,
                private utilService: UtilityService,
                public dialog: MatDialog,
                private appService: AppService,
                private ats: AnalyticsService) {

        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            overlayNoRowsTemplate: "No scheduled rebalances.",
            context: {componentParent: this},
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };
        
        this.frameworkComponents = this.gls.getFrameworkComponents();
    }

    ngOnInit(): void {
        let rowCount = this.scheduledBalances ? this.getRowCount() : 0;
        this.computeGridHeights(rowCount);
    }

    @Input()
    get data(): any {
        return this._data;
    }

    set data(d: any) {
        this._data = d;
        this._originalData = this.utilService.clone(this._data);
        this.setRebalancesData();
        this.loadGrid();
    }

    private setRebalancesData() {
        this.setIsUnauthorized();
        this.loadScheduledBalancesData();
        this.rowCount = this.getRowCount();
    }

    private loadScheduledBalancesData() {
        this.scheduledBalances = [];
        if (this.data && this.data.analysisSet && this.data.analysisSet.length > 0) {
            this.data.analysisSet.forEach((x: any) => {
                let item = JSON.parse(JSON.stringify(x));
                item.isSmartFiltered = false;
                this.scheduledBalances.push(item);
            });
        }
        if (this.data && this.data.sfAnalysisSet && this.data.sfAnalysisSet.length > 0) {
            this.data.sfAnalysisSet.forEach((x: any) => {
                let item = JSON.parse(JSON.stringify(x));
                item.isSmartFiltered = true;
                this.scheduledBalances.push(item);
            });
        }
        this.computeGridHeights(this.scheduledBalances.length);
    }

    private loadGrid() {
        if (this.grid && this.grid.api) {
            this.grid.api.setGridOption('rowData', this.scheduledBalances);
            this.rowCount = this.getRowCount();
            this.computeGridHeights(this.rowCount);
        }
    }

    public setIsUnauthorized() {
        if (this.data && this.data.authStatus && this.data.authStatus === 'Unauthorized') {
            this._isUnAuthorized = true;
        } else {
            this._isUnAuthorized = false;
        }
    }

    delete(params: any) {
        if (!params.isSmartFiltered) {
            params.isUIdeleted = true;
            let delItem = this._data.analysisSet.find((x: any) => x.analysisId === params.analysisId);
            delItem.isUIdeleted = true;
            this.change.emit(true);
            this.loadGrid();
        }
    }

    deleteAll() {
        let hasDeleted = false;
        if (this.scheduledBalances && this.scheduledBalances.length > 0) {
            this.scheduledBalances.forEach((params: any) => {
                if (!params.isSmartFiltered) {
                    params.isUIdeleted = true;
                    let delItem = this._data.analysisSet.find((x: any) => x.analysisId === params.analysisId);
                    delItem.isUIdeleted = true;
                    hasDeleted = true;
                }
            });
        }
        this.ctas.remove();
        if (hasDeleted) {
            this.loadGrid();
            this.change.emit(true);
        }
    }

    cancelDelete() {
        this.ctas.remove();
    }

    disableDelete() {
        return this._isUnAuthorized;
    }

    getRowCount() {
        return (this.scheduledBalances) ? this.scheduledBalances.filter((x: any) => x.isUIdeleted === false).length : 0;
    }

    onGridReady(params: any) {
        this.grid = params;
    }


    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    computeGridHeights(totalRecords: number) {
        let defaultHeights: any;
        if (totalRecords <= 9 && totalRecords > 0) {
            defaultHeights = {
                height: null,
                minHeight: null,
                maxHeight: null,
                domLayout: 'autoHeight'
            };
        } else {
            defaultHeights = {
                height: '200px',
                minHeight: '100px',
                maxHeight: '300px',
                domLayout: 'normal'
            };
        }
        this.defaultGridHeights = defaultHeights;
        if (this.grid && this.grid.api) {
            this.grid.api.setGridOption('domLayout', this.defaultGridHeights.domLayout);
        }

    }

    showEditRebalance(data: any) {
        this.fireRebalanceTypeEvent(data);
    }

    fireRebalanceTypeEvent(params: any) {
        this.ats.trackCreateRebalanceEvent();

        if (params.analysisType === "Harvest tax losses" || params.analysisType === "Tax Loss Harvest") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_HARVEST_TAX_LOSS_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Fully rebalance securities & cash" || params.analysisType === "Full rebalance") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_EDIT_CREATE_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Generate and/or invest cash") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_GENERATE_INVEST_CASH_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Invest cash only") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_INVEST_CASH_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Generate cash only") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_GENERATE_CASH_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Replace securities") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_REPLACE_SECURITIES_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
        if (params.analysisType === "Adjust securities") {
            this.appService.fireEvent(IrbRebalanceEvents.SHOW_ADJUST_SECURITIES_REBALANCE, {
                fullscreen: true,
                analysisId: params.analysisId,
                source: 'Scheduled',
                slot:"B"
            });
        }
    }

    onIncludeInaScheduledRebalance() {
        let portfolioData = JSON.parse(JSON.stringify(this._data));
        let scheduledRebalances = JSON.parse(JSON.stringify(this.scheduledBalances));
        let dialogData = {
            scheduledRebalances: scheduledRebalances,
            portfolioData: portfolioData,
            continueFunction: this.addScheduledRebalances.bind(this)
        };
        this.showIncludePortfolioInRebalanceDialog(dialogData);
    }
    showIncludePortfolioInRebalanceDialog(data: any) {
        const config = new MatDialogConfig();
        config.disableClose = true;
        config.panelClass = "irb-dialog";
        config.autoFocus = false;
        config.minHeight = "100px";
        config.height = "auto";
        config.width = "750px";
        config.data = data;
        this.dialog.open(ComIncludeInScheduledRebalanceDialogComponent, config);
    }

    addScheduledRebalances(rebalances: any[]) {
        if (rebalances && rebalances.length > 0) {
            let smartFilteredRecords: any[] = [];
            rebalances.forEach((rebalance: any) => {
                let existingRecord: any = this.scheduledBalances.find((x: any) => x.analysisId === rebalance.analysisId);
                if (!existingRecord) {
                    rebalance.isSmartFiltered = false;
                    rebalance.isNew = true;
                    smartFilteredRecords.push(rebalance);
                }
            });
            if(smartFilteredRecords && smartFilteredRecords.length > 0) {
                let newScheduledBalanceRecords = JSON.parse(JSON.stringify(smartFilteredRecords));
                newScheduledBalanceRecords.forEach((x:any)=>{
                   delete x.isSmartFiltered;
                });

                this.scheduledBalances.push(...smartFilteredRecords);
                this._data.analysisSet.push(...newScheduledBalanceRecords);
                this.change.emit(true);
                this.loadGrid();
            }
        }
    }
}
